import React, { memo, useCallback } from "react";
import { Col, Form, Row } from "reactstrap";
import { JobWarning } from "../../../../../common/api/v1.1/types/jobs";
import { StateCallbacks } from "../../state";
import { buildValueKey, getConfidenceState } from "../../state/util";
import { Block, DataPointValue, NonGroupTaxonomyEntry, State } from "../../types";
import { Answer } from "../Answer";
import { QuestionTitle } from "./QuestionTitle";
import { useExtractNonGroupValues } from "./useExtractNonGroupValues";

type Props = Readonly<{
  active: State["activeValue"];
  confidenceThreshold: State["confidenceThreshold"];
  dataPoint: NonGroupTaxonomyEntry;
  review: boolean;
  stateCallbacks: StateCallbacks;
  values: State["values"];
  blocks: {
    [blockId: string]: Block;
  };
  focusWarningsTab: () => void;
  warnings: JobWarning[];
}>;

const newKey = (lastDataPoint: DataPointValue<any>, dataPointKey: string): string => {
  const lastIndex = parseInt(lastDataPoint.valueKey.split(".")[1], 10);
  return buildValueKey(null, null, dataPointKey, lastIndex + 1);
};

const QuestionComponent = ({
  active,
  confidenceThreshold,
  dataPoint,
  review,
  stateCallbacks,
  values,
  blocks,
  warnings,
  focusWarningsTab
}: Props) => {
  const onSubmit = useCallback(e => {
    e.preventDefault();
  }, []);
  const dataPointValueKeys = useExtractNonGroupValues(dataPoint.id, dataPoint.multiple, values);
  const disableAdd = !dataPoint.multiple;
  const disableRemove = !dataPoint.multiple || dataPointValueKeys.length < 2;
  return (
    <Form onSubmit={onSubmit}>
      <QuestionTitle label={dataPoint.question} />
      <Row>
        <Col className="question-container" xs={12}>
          {dataPointValueKeys.map(k => {
            const value = values[k];
            return (
              <Answer
                active={active === value.valueKey}
                confidenceState={getConfidenceState(confidenceThreshold)(value)}
                dataPoint={dataPoint}
                disableAdd={disableAdd}
                disableRemove={disableRemove}
                id={value.valueKey}
                key={value.valueKey}
                label={dataPoint.question}
                onAddValue={() => {
                  if (!dataPoint.multiple) {
                    return;
                  }
                  stateCallbacks.onAddValue(
                    dataPoint.id,
                    newKey(
                      values[dataPointValueKeys[dataPointValueKeys.length - 1]],
                      dataPoint.id,
                    ),
                  );
                }}
                onDuplicateValue={() => {
                  if (!dataPoint.multiple) {
                    return;
                  }
                  stateCallbacks.onDuplicateAnswer(
                    dataPoint.id,
                    newKey(
                      values[dataPointValueKeys[dataPointValueKeys.length - 1]],
                      dataPoint.id,
                    ),
                    value,
                  );
                }}
                onFocus={stateCallbacks.onSelectDataPoint}
                onRemove={
                  disableRemove ? stateCallbacks.onClearValue : stateCallbacks.onRemoveAnswer
                }
                onScrollToBlock={stateCallbacks.onSetScrollToBlock}
                onUpdate={stateCallbacks.onUpdateValue}
                review={review}
                showLabel={false}
                showMultiple={dataPoint.multiple}
                value={value}
                focusWarningsTab={focusWarningsTab}
                hasWarning={!!warnings && !!warnings.find(x => x.dataPoint === value.valueKey)}
                block={blocks[value?.idRef as string]}
                />
            );
          })}
        </Col>
      </Row>
    </Form>
  );
};
QuestionComponent.displayName = "ActiveQuestion";
export const Question = memo(QuestionComponent);
